import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import pluralize from 'pluralize';
import { Button, Row, Col, Modal, ModalProps, Stack } from 'react-bootstrap';
import { Form, Formik, FormikHelpers } from 'formik';
import DataTable, { TableColumn } from 'react-data-table-component';
import { selectIsFrontline } from '@components/Auth';
import { FormGroupControl } from '@components/Base';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { getAttachmentFilename } from '@utils/attachment';
import { ClaimableTypes } from '@models/InsuranceClaim';
import { selectHealthClaim } from './selector';
import { HealthClaimUploadDocumentsPayload, healthClaimUploadDocuments } from './action';
import { useCanRequestDocuments } from '../hook';
import { RequestDocumentsModal } from '../RequestDocumentsModal';

import { object, InferType } from 'yup';
import { yupMultipleFiles } from '@utils/yup';

const GROUP_PROPS = { className: 'mb-3' };

const validationSchema = object({
  medicalDocuments: yupMultipleFiles({ required: false }),
  identityDocuments: yupMultipleFiles({ required: false }),
  settlementLetters: yupMultipleFiles({ required: false }),
});

type FormValues = InferType<typeof validationSchema>;

type UploadDocumentsModalProps = ModalProps & {
  isFrontline: boolean;
  healthClaimId: number;
  onSubmit: () => void;
};

const UploadDocumentsModal = ({ isFrontline, healthClaimId, onSubmit, ...props }: UploadDocumentsModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const initialValues: FormValues = validationSchema.getDefault();

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const payload: HealthClaimUploadDocumentsPayload = {
      ...values,
      id: Number(healthClaimId),
    };
    dispatch(healthClaimUploadDocuments(payload));
    setSubmitting(false);

    onSubmit();
  };

  return (
    <Modal {...props} size="lg" centered>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ values, isSubmitting }) => {
          const { medicalDocuments, identityDocuments, settlementLetters } = values;
          let allEmpty = medicalDocuments.length === 0 && identityDocuments.length === 0;

          if (isFrontline) {
            allEmpty = allEmpty && settlementLetters.length === 0;
          }

          return (
            <Form>
              <Modal.Header closeButton>
                <Modal.Title>{t('common:uploadDocuments')}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Row>
                  <Col>
                    <FormGroupControl
                      multiple
                      type="file"
                      name="medicalDocuments"
                      label={t('healthInsuranceClaimForm.injuryOrIllnessSection.medicalDocuments.label')}
                      groupProps={GROUP_PROPS}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroupControl
                      multiple
                      type="file"
                      name="identityDocuments"
                      label={t('healthInsuranceClaimForm.injuryOrIllnessSection.identityDocuments.label')}
                      groupProps={GROUP_PROPS}
                    />
                  </Col>
                </Row>
                {isFrontline && (
                  <Row>
                    <Col>
                      <FormGroupControl
                        multiple
                        type="file"
                        name="settlementLetters"
                        label={`Attach settlement letters`}
                        groupProps={GROUP_PROPS}
                      />
                    </Col>
                  </Row>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={props.onHide}>
                  {t('confirmModal.cancel')}
                </Button>
                <Button type="submit" variant="primary" disabled={allEmpty || isSubmitting}>
                  {t('common:submit')}
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

const DocumentsTabContent = () => {
  const { t } = useTranslation();
  const [showUploadDocumentsModal, setShowUploadDocumentsModal] = useState(false);
  const [showRequestDocumentsModal, setShowRequestDocumentsModal] = useState(false);
  const { healthClaim } = useSelector(selectHealthClaim);
  const isFrontline = useSelector(selectIsFrontline);
  const canRequestDocuments = useCanRequestDocuments();
  const documents = healthClaim?.medicalDocuments
    .concat(healthClaim?.identityDocuments, healthClaim?.settlementLetters)
    .map((url) => ({
      name: getAttachmentFilename(url) as string,
      url,
    }));

  const columns = useMemo(
    () =>
      [
        {
          name: t('insuranceClaimDetails.document.name'),
          selector: (row) => row.name,
        },
        {
          name: pluralize(t('insuranceClaimDetails.document.action')),
          cell: (row) => (
            <Button size="sm" variant="outline-primary" target="_blank" rel="noreferrer noopener" href={row.url}>
              <FontAwesomeIcon icon={faEye} className="fa-fw me-2" />
              {t('common:viewDetails')}
            </Button>
          ),
        },
      ] as TableColumn<{ name: string; url: string }>[],
    [],
  );

  const customStyles = {
    headCells: {
      style: {
        fontWeight: 'bold',
      },
    },
  };

  return (
    <>
      <DataTable columns={columns} data={documents ?? []} customStyles={customStyles} fixedHeader />
      <Stack direction="horizontal" gap={2} className="mt-5">
        <Button onClick={() => setShowUploadDocumentsModal(true)}>{t('common:uploadDocuments')}</Button>
        {canRequestDocuments && (
          <Button variant="outline-primary" onClick={() => setShowRequestDocumentsModal(true)}>
            {t('common:requestDocuments')}
          </Button>
        )}
      </Stack>

      {healthClaim && (
        <UploadDocumentsModal
          isFrontline={isFrontline}
          healthClaimId={healthClaim.id}
          show={showUploadDocumentsModal}
          onHide={() => setShowUploadDocumentsModal(false)}
          onSubmit={() => setShowUploadDocumentsModal(false)}
        />
      )}

      {healthClaim && (
        <RequestDocumentsModal
          isOpen={showRequestDocumentsModal}
          onClose={() => setShowRequestDocumentsModal(false)}
          claimableType={ClaimableTypes.Health}
          claimableId={healthClaim.id}
          claimNumber={healthClaim.claimNumber}
        />
      )}
    </>
  );
};

export { DocumentsTabContent };
